/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation, useNavigate, useParams } from 'js/lib/useRouter';

import { Grid, Tab, TabContext, Tabs, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import GoToDegreeAdmin from 'bundles/degree-home/components/GoToDegreeAdmin';
import { PATHS } from 'bundles/degree-home/components/v3-dpm/constants/constants';

import _t from 'i18n!nls/degree-home';

type Props = {
  isDPMEnabled: boolean;
  children: JSX.Element;
};

const styles = {
  tabNavigationContainer: () => css`
    border-bottom: 2px solid var(--cds-color-grey-50);
  `,

  tabNavigation: css`
    margin-bottom: -1px;
    border-bottom: none;
  `,

  buttonContainer: (_: Theme, buttonVisible: boolean) => css`
    display: flex;
    padding: var(--cds-spacing-100) 0;

    ${breakpoints.down('sm')} {
      padding: ${buttonVisible ? 'var(--cds-spacing-100)' : 0};

      button {
        width: 100%;
      }
    }
  `,

  contentContainer: () => css`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 var(--cds-spacing-600);

    ${breakpoints.down('xs')} {
      padding: 0;
    }
  `,
};

const TabsContainer = ({ isDPMEnabled, children }: Props) => {
  const { degreeSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lastPathSegment = location.pathname.split('/').pop();
  const defaultTab = isDPMEnabled ? PATHS.IN_PROGRESS : PATHS.HOME;

  const [selectedTab, setSelectedTab] = React.useState<string>(defaultTab);

  React.useEffect(() => {
    if (lastPathSegment) {
      setSelectedTab(lastPathSegment);
    }
  }, [lastPathSegment]);

  const handleTabChange = (newValue: string) => {
    setSelectedTab(newValue);
    navigate({
      pathname: `/degrees/${degreeSlug}/${newValue}`,
    });
  };

  const tabsProps = {
    css: styles.tabNavigation,
    'aria-label': _t('Degree home navigation'),
    onChange: handleTabChange,
  };

  return (
    <TabContext value={selectedTab}>
      <div css={styles.tabNavigationContainer}>
        <Grid container css={styles.contentContainer}>
          <Grid item xs={12} sm={6}>
            {isDPMEnabled ? (
              <Tabs {...tabsProps}>
                <Tab label={_t('In Progress')} value={PATHS.IN_PROGRESS} />
                <Tab label={_t('Academics')} value={PATHS.ACADEMICS} />
              </Tabs>
            ) : (
              <Tabs {...tabsProps}>
                <Tab label={_t('In Progress')} value="home" data-e2e="degree-home-tab-link" />
                <Tab label={_t('Summary')} value="summary" data-e2e="degree-summary-tab-link" />
              </Tabs>
            )}
          </Grid>

          <Grid
            item
            container
            xs={12}
            sm={6}
            justifyContent="flex-end"
            alignItems="center"
            flex-direction="row"
            spacing={16}
          >
            <Grid item>
              <GoToDegreeAdmin />
            </Grid>
          </Grid>
        </Grid>
      </div>

      <main id="main" css={styles.contentContainer}>
        {children}
      </main>
    </TabContext>
  );
};

export default TabsContainer;
